import { Alert, Paper, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import TargetVSActualPreviousMonth from "./AdditionalQueriesAccordion/TargetVsActualPreviousMonth";
import { TrendsOverPastSevenDays } from "./AdditionalQueriesAccordion/TrendsOverPastSevenDays";
import { BrandPerformance } from "./AdditionalQueriesAccordion/BrandPerformance";
import { QFTDBudgetControl } from "./AdditionalQueriesAccordion/QFTDBudgetControl";
import { TopKeywords } from "./AdditionalQueriesAccordion/TopKeywords";
import { ConversionLoadingPercentage } from "./AdditionalQueriesAccordion/ConversionLoadingPercentage";
import { SitePerformance } from "./AdditionalQueriesAccordion/SitePerformance";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "./../BusinessLeaderDashboard.css";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React from "react";
import { BLDTableSkeleton } from "../helpers/BLDSkeletons";
import {
  additionalQueriestableHeadings01,
  additionalQueriestableHeadings02,
  additionalQueriestableHeadings04,
  additionalQueriestableHeadings06,
} from "./AdditionalQueriesAccordion/AdditionalQueriesConstants";

interface AdditionalQueriesProps {
  lastMonthData: any;
  last7DaysData: any;
  mtdQftdData: any;
  conversionData: any;
  collapseAllSections: any;
  setCollapseAllSections: any;
  /**
   * Changes on every "Get Data" click. Used as a key on the sections that fetch
   * themselves, so they remount and re-query with the new selection.
   */
  fetchToken: number;
  isLastMonthLoading: boolean;
  isLast7DaysLoading: boolean;
  isMtdQftdLoading: boolean;
  isConversionLoading: boolean;
}

export const AdditionalQueries: React.FC<AdditionalQueriesProps> = React.memo(
  ({
    lastMonthData,
    last7DaysData,
    mtdQftdData,
    conversionData,
    collapseAllSections,
    setCollapseAllSections,
    fetchToken,
    isLastMonthLoading,
    isLast7DaysLoading,
    isMtdQftdLoading,
    isConversionLoading,
  }) => {
    const [expanded, setExpanded] = useState([]) as any;

    const handleChange = (panel: any) => (_: unknown, isExpanded: any) => {
      setExpanded((prev: any) =>
        isExpanded ? [...prev, panel] : prev.filter((p: any) => p !== panel)
      );
    };

    useEffect(() => {
      if (collapseAllSections) {
        setExpanded([]);
        setCollapseAllSections(false);
      }
      // eslint-disable-next-line
    }, [collapseAllSections]);

    const AccordionSummaryStyle = {
      borderBottom: "2px solid rgba(0, 0, 0, 0.12)",
    };

    return (
      <>
        <Paper sx={{ padding: "8px 16px", mt: 1, boxShadow: "none" }}>
          <Typography fontSize={24}>Additional Queries:</Typography>
        </Paper>

        <Accordion
          className="AccordionWrap"
          sx={{ m: "0 !important" }}
          expanded={expanded?.includes("TargetVSActualPreviousMonth")}
          onChange={handleChange("TargetVSActualPreviousMonth")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
            sx={AccordionSummaryStyle}
          >
            <Typography component="span" sx={{ width: "33%", flexShrink: 0 }}>
              Target VS. Actual - Previous Month
            </Typography>
            <Typography component="span" sx={{ color: "text.secondary" }}>
              Performance compared to the goals set for the previous month
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {isLastMonthLoading ? (
              <BLDTableSkeleton
                columns={Object.keys(additionalQueriestableHeadings01).length}
              />
            ) : lastMonthData?.length > 0 ? (
              <TargetVSActualPreviousMonth
                lastMonthTargetVsActualData={lastMonthData}
              />
            ) : (
              <Alert
                sx={{ color: "#ED6C02", mb: 1 }}
                severity="warning"
                icon={<InfoOutlinedIcon fontSize="inherit" sx={{ mr: 1 }} />}
              >
                NO DATA
              </Alert>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion
          className="AccordionWrap"
          sx={{ m: "0 !important" }}
          expanded={expanded?.includes("TrendsOverPastSevenDays")}
          onChange={handleChange("TrendsOverPastSevenDays")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel2bh-content"
            id="panel2bh-header"
            sx={AccordionSummaryStyle}
          >
            <Typography component="span" sx={{ width: "33%", flexShrink: 0 }}>
              Trends over the past 7 days
            </Typography>
            <Typography component="span" sx={{ color: "text.secondary" }}>
              The last 7 days, excluding the past 2 days
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {isLast7DaysLoading ? (
              <BLDTableSkeleton
                columns={Object.keys(additionalQueriestableHeadings02).length}
              />
            ) : last7DaysData?.length > 0 ? (
              <TrendsOverPastSevenDays
                last7DaysSince2DaysAgoData={last7DaysData}
              />
            ) : (
              <Alert
                sx={{ color: "#ED6C02", mb: 1 }}
                severity="warning"
                icon={<InfoOutlinedIcon fontSize="inherit" sx={{ mr: 1 }} />}
              >
                NO DATA
              </Alert>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion
          className="AccordionWrap"
          sx={{ m: "0 !important" }}
          expanded={expanded?.includes("BrandPerformance")}
          onChange={handleChange("BrandPerformance")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel3bh-content"
            id="panel3bh-header"
            sx={AccordionSummaryStyle}
          >
            <Typography component="span" sx={{ width: "33%", flexShrink: 0 }}>
              Brand Performance
            </Typography>
            <Typography component="span" sx={{ color: "text.secondary" }}>
              Essential performance metrics by brand
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <BrandPerformance key={fetchToken} />
          </AccordionDetails>
        </Accordion>

        <Accordion
          className="AccordionWrap"
          sx={{ m: "0 !important" }}
          expanded={expanded?.includes("QFTDBudgetControl")}
          onChange={handleChange("QFTDBudgetControl")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel4bh-content"
            id="panel4bh-header"
            sx={AccordionSummaryStyle}
          >
            <Typography component="span" sx={{ width: "33%", flexShrink: 0 }}>
              QFTD budget control
            </Typography>
            <Typography component="span" sx={{ color: "text.secondary" }}>
              Accroding to user's choice
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {isMtdQftdLoading ? (
              <BLDTableSkeleton
                columns={Object.keys(additionalQueriestableHeadings04).length}
              />
            ) : mtdQftdData?.length > 0 ? (
              <QFTDBudgetControl mtdQftdBudgetControlIdData={mtdQftdData} />
            ) : (
              <Alert
                sx={{
                  color: "#ED6C02",
                  margin: "16px",
                  border: "1px solid #ED6C0280",
                }}
                severity="warning"
                icon={<InfoOutlinedIcon fontSize="inherit" sx={{ mr: 1 }} />}
              >
                NO DATA
              </Alert>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion
          className="AccordionWrap"
          sx={{ m: "0 !important" }}
          expanded={expanded?.includes("TopKeywords")}
          onChange={handleChange("TopKeywords")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel5bh-content"
            id="panel5bh-header"
            sx={AccordionSummaryStyle}
          >
            <Typography component="span" sx={{ width: "33%", flexShrink: 0 }}>
              Top Keywords
            </Typography>
            <Typography component="span" sx={{ color: "text.secondary" }}>
              Best - performing keyword driving traffic, conversions, or revenue
              Per website.
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <TopKeywords key={fetchToken} />
          </AccordionDetails>
        </Accordion>

        <Accordion
          className="AccordionWrap"
          sx={{ m: "0 !important" }}
          expanded={expanded?.includes("ConversionLoadingPercentage")}
          onChange={handleChange("ConversionLoadingPercentage")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel6bh-content"
            id="panel6bh-header"
            sx={AccordionSummaryStyle}
          >
            <Typography component="span" sx={{ width: "33%", flexShrink: 0 }}>
              Conversion Loading Percentage
            </Typography>
            <Typography component="span" sx={{ color: "text.secondary" }}>
              14 - day conversion loading rate for FTDs or QFTDs to ad platforms
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {isConversionLoading ? (
              <BLDTableSkeleton
                columns={Object.keys(additionalQueriestableHeadings06).length}
              />
            ) : conversionData?.length > 0 ? (
              <ConversionLoadingPercentage
                conversionLoadingPercLast14DaysData={conversionData}
              />
            ) : (
              <Alert
                sx={{
                  color: "#ED6C02",
                  margin: "16px",
                  border: "1px solid #ED6C0280",
                }}
                severity="warning"
                icon={<InfoOutlinedIcon fontSize="inherit" sx={{ mr: 1 }} />}
              >
                NO DATA
              </Alert>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion
          className="AccordionWrap"
          sx={{ m: "0 !important" }}
          expanded={expanded?.includes("SitePerformance")}
          onChange={handleChange("SitePerformance")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel7bh-content"
            id="panel7bh-header"
            sx={AccordionSummaryStyle}
          >
            <Typography component="span" sx={{ width: "33%", flexShrink: 0 }}>
              Site performance
            </Typography>
            <Typography component="span" sx={{ color: "text.secondary" }}>
              End-to-end data of the entire marketing funnel
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <SitePerformance key={fetchToken} />
          </AccordionDetails>
        </Accordion>
      </>
    );
  }
);
